// src/components/rituals/DailyAlignment.tsx
// Morning and Evening Alignment Rituals

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Sparkles, ChevronRight, Check, X } from 'lucide-react';
import { useSoulFile } from '../../hooks/useSoulFile';
import { supabase } from '../../lib/supabase/browserClient';
import { useAuth } from '../../hooks/useAuth';

type RitualType = 'morning' | 'evening';
type RitualStep = 'intro' | 'intention' | 'affirmation' | 'action' | 'reflection' | 'gratitude' | 'release' | 'evidence' | 'complete';

interface DailyAlignmentProps {
  type: RitualType;
  onComplete: () => void;
  onClose: () => void;
  embedded?: boolean;
}

// Affirmations pool (in production, these would be personalized via AI)
const AFFIRMATIONS = [
  "I am becoming the person I want to be, one choice at a time.",
  "I am worthy of the life I'm creating.",
  "I trust the timing of my journey.",
  "I release what's not mine to carry.",
  "I am open to receiving all that's aligned with my highest good.",
  "My desires are valid and I honor them.",
  "I choose to show up for myself today.",
  "I am more capable than I give myself credit for.",
  "The universe is conspiring in my favor.",
  "I am exactly where I need to be."
];

// Higher Self Actions (would be personalized)
const HIGHER_SELF_ACTIONS = [
  "Speak up about something you've been holding back",
  "Say no to one thing that doesn't feel aligned",
  "Reach out to someone you've been thinking about",
  "Take 10 minutes to do something just for you",
  "Make a decision you've been avoiding",
  "Share something you created with one person",
  "Set one boundary today",
  "Celebrate a recent win (even a small one)",
  "Ask for help with something",
  "Do one thing that scares you a little"
];

export function DailyAlignment({ type, onComplete, onClose, embedded = false }: DailyAlignmentProps) {
  const { user } = useAuth();
  const { soulFile } = useSoulFile();
  const [currentStep, setCurrentStep] = useState<RitualStep>('intro');
  const [responses, setResponses] = useState<Record<string, string | string[]>>({});
  const [todaysAffirmation] = useState(() => AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)]);
  const [todaysAction] = useState(() => HIGHER_SELF_ACTIONS[Math.floor(Math.random() * HIGHER_SELF_ACTIONS.length)]);
  const [gratitudeItems, setGratitudeItems] = useState<string[]>(['', '', '']);
  const [evidenceInput, setEvidenceInput] = useState('');

  const isMorning = type === 'morning';
  const steps: RitualStep[] = isMorning
    ? ['intro', 'intention', 'affirmation', 'action', 'complete']
    : ['intro', 'reflection', 'evidence', 'gratitude', 'release', 'complete'];

  const wrapperClass = embedded
    ? `flex-1 overflow-y-auto ${
        isMorning
          ? 'bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50'
          : 'bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950'
      }`
    : `min-h-screen ${
        isMorning
          ? 'bg-gradient-to-br from-amber-50 via-orange-50 to-pink-50'
          : 'bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950'
      }`;

  const handleNext = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const handleSaveAndComplete = async () => {
    if (!user?.id) return;

    const today = new Date().toISOString().split('T')[0];

    try {
      // Check if today's alignment exists
      const { data: existing } = await supabase
        .from('daily_alignments')
        .select('id')
        .eq('user_id', user.id)
        .eq('date', today)
        .single();

      const updates: Record<string, any> = isMorning ? {
        morning_completed: true,
        morning_completed_at: new Date().toISOString(),
        intention: responses.intention || null,
        affirmation: todaysAffirmation,
        higher_self_action: todaysAction
      } : {
        evening_completed: true,
        evening_completed_at: new Date().toISOString(),
        reflections: [responses.reflection].filter(Boolean),
        evidence_collected: evidenceInput ? [evidenceInput] : [],
        gratitude: gratitudeItems.filter(g => g.trim()),
        release_note: responses.release || null
      };

      if (existing) {
        await supabase
          .from('daily_alignments')
          .update(updates)
          .eq('id', existing.id);
      } else {
        await supabase
          .from('daily_alignments')
          .insert({
            user_id: user.id,
            date: today,
            ...updates
          });
      }

      // Log activity for streak
      await supabase.from('activity_log').insert({
        user_id: user.id,
        activity_type: isMorning ? 'morning-alignment' : 'evening-reflection',
        metadata: { date: today }
      });

      setCurrentStep('complete');
    } catch (error) {
      console.error('Error saving alignment:', error);
    }
  };

  return (
    <div className={wrapperClass}>
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <button
          onClick={onClose}
          className={`p-2 rounded-full ${isMorning ? 'text-amber-600 hover:bg-amber-100' : 'text-purple-300 hover:bg-white/10'}`}
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="flex items-center gap-2">
          {isMorning ? (
            <Sun className="w-5 h-5 text-amber-500" />
          ) : (
            <Moon className="w-5 h-5 text-purple-400" />
          )}
          <span className={isMorning ? 'text-amber-700 font-medium' : 'text-purple-200 font-medium'}>
            {isMorning ? 'Morning Alignment' : 'Evening Reflection'}
          </span>
        </div>

        {/* Progress dots */}
        <div className="flex gap-1.5">
          {steps.map((step, i) => (
            <div 
              key={step}
              className={`w-2 h-2 rounded-full transition-colors ${
                steps.indexOf(currentStep) >= i 
                  ? (isMorning ? 'bg-amber-500' : 'bg-purple-400')
                  : (isMorning ? 'bg-amber-200' : 'bg-purple-800')
              }`}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-xl mx-auto px-4 pt-8 pb-20">
        <AnimatePresence mode="wait">
          {/* Intro */}
          {currentStep === 'intro' && (
            <StepContainer key="intro" isMorning={isMorning}>
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="inline-block mb-6"
                >
                  {isMorning ? (
                    <Sun className="w-16 h-16 text-amber-400" />
                  ) : (
                    <Moon className="w-16 h-16 text-purple-400" />
                  )}
                </motion.div>
                
                <h1 className={`text-2xl font-bold mb-3 ${isMorning ? 'text-amber-900' : 'text-white'}`}>
                  {isMorning ? 'Good morning' : 'Good evening'}
                  {soulFile?.essence.preferredName ? `, ${soulFile.essence.preferredName}` : ''} ✨
                </h1>
                
                <p className={`mb-8 ${isMorning ? 'text-amber-700/80' : 'text-purple-200/80'}`}>
                  {isMorning 
                    ? "Let's set the tone for your day." 
                    : "Let's reflect on your day and prepare for rest."}
                </p>

                <button
                  onClick={handleNext}
                  className={`px-8 py-3 rounded-xl font-medium ${
                    isMorning 
                      ? 'bg-amber-500 text-white hover:bg-amber-600' 
                      : 'bg-purple-500 text-white hover:bg-purple-600'
                  }`}
                >
                  Begin
                </button>
              </div>
            </StepContainer>
          )}

          {/* Morning: Intention */}
          {currentStep === 'intention' && (
            <StepContainer key="intention" isMorning={isMorning}>
              <h2 className={`text-xl font-semibold mb-2 ${isMorning ? 'text-amber-900' : 'text-white'}`}>
                Set Your Intention
              </h2>
              <p className={`mb-6 ${isMorning ? 'text-amber-700/80' : 'text-purple-200/80'}`}>
                How do you want to feel today? What energy are you choosing?
              </p>
              
              <textarea
                value={responses.intention as string || ''}
                onChange={(e) => setResponses(prev => ({ ...prev, intention: e.target.value }))}
                placeholder="Today, I intend to feel..."
                className={`w-full p-4 rounded-xl border-2 resize-none ${
                  isMorning 
                    ? 'bg-white/70 border-amber-200 focus:border-amber-400 text-amber-900 placeholder-amber-400'
                    : 'bg-white/5 border-purple-400/30 focus:border-purple-400 text-white placeholder-purple-300/50'
                } focus:outline-none`}
                rows={4}
              />

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleNext}
                  disabled={!responses.intention}
                  className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
                    isMorning 
                      ? 'bg-amber-500 text-white hover:bg-amber-600 disabled:bg-amber-200' 
                      : 'bg-purple-500 text-white hover:bg-purple-600 disabled:bg-purple-800'
                  }`}
                >
                  Continue
                </button>
              </div>
            </StepContainer>
          )}

          {/* Morning: Affirmation */}
          {currentStep === 'affirmation' && (
            <StepContainer key="affirmation" isMorning={isMorning}>
              <h2 className={`text-xl font-semibold mb-2 ${isMorning ? 'text-amber-900' : 'text-white'}`}>
                Your Affirmation for Today
              </h2>
              <p className={`mb-6 ${isMorning ? 'text-amber-700/80' : 'text-purple-200/80'}`}>
                Read this aloud, or let it sink in quietly:
              </p>

              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`p-6 rounded-2xl text-center ${
                  isMorning 
                    ? 'bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200'
                    : 'bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-400/30'
                }`}
              >
                <Sparkles className={`w-8 h-8 mx-auto mb-4 ${isMorning ? 'text-amber-500' : 'text-purple-400'}`} />
                <p className={`text-xl font-medium italic ${isMorning ? 'text-amber-900' : 'text-white'}`}>
                  "{todaysAffirmation}"
                </p>
              </motion.div>

              <button
                onClick={handleNext}
                className={`w-full mt-6 py-3 rounded-xl font-medium ${
                  isMorning 
                    ? 'bg-amber-500 text-white hover:bg-amber-600' 
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                I receive this ✨
              </button>
            </StepContainer>
          )}

          {/* Morning: Higher Self Action */}
          {currentStep === 'action' && (
            <StepContainer key="action" isMorning={isMorning}>
              <h2 className={`text-xl font-semibold mb-2 ${isMorning ? 'text-amber-900' : 'text-white'}`}>
                Your Higher Self Action
              </h2>
              <p className={`mb-6 ${isMorning ? 'text-amber-700/80' : 'text-purple-200/80'}`}>
                One small thing your future self would do today:
              </p>

              <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                className={`p-5 rounded-2xl ${
                  isMorning 
                    ? 'bg-white/80 border border-amber-200'
                    : 'bg-white/5 border border-purple-400/30'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${isMorning ? 'bg-amber-100' : 'bg-purple-500/20'}`}>
                    <ChevronRight className={`w-5 h-5 ${isMorning ? 'text-amber-600' : 'text-purple-400'}`} />
                  </div>
                  <p className={`text-lg ${isMorning ? 'text-amber-900' : 'text-white'}`}>
                    {todaysAction}
                  </p>
                </div>
              </motion.div>

              <p className={`mt-4 text-sm ${isMorning ? 'text-amber-600/70' : 'text-purple-300/60'}`}>
                This doesn't have to be big. Small aligned actions compound.
              </p>

              <button
                onClick={handleSaveAndComplete}
                className={`w-full mt-6 py-3 rounded-xl font-medium ${
                  isMorning 
                    ? 'bg-amber-500 text-white hover:bg-amber-600' 
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                I'm ready for today ✨
              </button>
            </StepContainer>
          )}

          {/* Evening: Reflection */}
          {currentStep === 'reflection' && (
            <StepContainer key="reflection" isMorning={isMorning}>
              <h2 className={`text-xl font-semibold mb-2 ${isMorning ? 'text-amber-900' : 'text-white'}`}>
                How was your day?
              </h2>
              <p className={`mb-6 ${isMorning ? 'text-amber-700/80' : 'text-purple-200/80'}`}>
                No judgment — just noticing.
              </p>
              
              <textarea
                value={responses.reflection as string || ''}
                onChange={(e) => setResponses(prev => ({ ...prev, reflection: e.target.value }))}
                placeholder="Today felt..."
                className="w-full p-4 rounded-xl border-2 resize-none bg-white/5 border-purple-400/30 focus:border-purple-400 text-white placeholder-purple-300/50 focus:outline-none"
                rows={4}
              />

              <button
                onClick={handleNext}
                disabled={!responses.reflection}
                className="w-full mt-6 py-3 rounded-xl font-medium bg-purple-500 text-white hover:bg-purple-600 disabled:bg-purple-800"
              >
                Continue
              </button>
            </StepContainer>
          )}

          {/* Evening: Evidence */}
          {currentStep === 'evidence' && (
            <StepContainer key="evidence" isMorning={isMorning}>
              <h2 className="text-xl font-semibold mb-2 text-white">
                Evidence of Growth
              </h2>
              <p className="mb-6 text-purple-200/80">
                Did you notice any proof today that you're changing? Big or small.
              </p>
              
              <textarea
                value={evidenceInput}
                onChange={(e) => setEvidenceInput(e.target.value)}
                placeholder="Today I noticed..."
                className="w-full p-4 rounded-xl border-2 resize-none bg-white/5 border-purple-400/30 focus:border-purple-400 text-white placeholder-purple-300/50 focus:outline-none"
                rows={3}
              />

              <p className="mt-2 text-xs text-purple-300/60">
                Examples: I set a boundary, I didn't spiral, I asked for what I needed
              </p>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleNext}
                  className="flex-1 py-3 rounded-xl font-medium bg-white/10 text-purple-200 hover:bg-white/15"
                >
                  Skip
                </button>
                <button
                  onClick={handleNext}
                  className="flex-1 py-3 rounded-xl font-medium bg-purple-500 text-white hover:bg-purple-600"
                >
                  Save & Continue
                </button>
              </div>
            </StepContainer>
          )}

          {/* Evening: Gratitude */}
          {currentStep === 'gratitude' && (
            <StepContainer key="gratitude" isMorning={isMorning}>
              <h2 className="text-xl font-semibold mb-2 text-white">
                Three Things
              </h2>
              <p className="mb-6 text-purple-200/80">
                What are you grateful for today?
              </p>
              
              <div className="space-y-3">
                {gratitudeItems.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-purple-400">{i + 1}.</span>
                    <input
                      value={item}
                      onChange={(e) => {
                        const newItems = [...gratitudeItems];
                        newItems[i] = e.target.value;
                        setGratitudeItems(newItems);
                      }}
                      placeholder={`Gratitude ${i + 1}...`}
                      className="flex-1 p-3 rounded-xl border-2 bg-white/5 border-purple-400/30 focus:border-purple-400 text-white placeholder-purple-300/50 focus:outline-none"
                    />
                  </div>
                ))}
              </div>

              <button
                onClick={handleNext}
                className="w-full mt-6 py-3 rounded-xl font-medium bg-purple-500 text-white hover:bg-purple-600"
              >
                Continue
              </button>
            </StepContainer>
          )}

          {/* Evening: Release */}
          {currentStep === 'release' && (
            <StepContainer key="release" isMorning={isMorning}>
              <h2 className="text-xl font-semibold mb-2 text-white">
                Release
              </h2>
              <p className="mb-6 text-purple-200/80">
                What are you ready to let go of from today? It doesn't need to follow you into sleep.
              </p>
              
              <textarea
                value={responses.release as string || ''}
                onChange={(e) => setResponses(prev => ({ ...prev, release: e.target.value }))}
                placeholder="I release..."
                className="w-full p-4 rounded-xl border-2 resize-none bg-white/5 border-purple-400/30 focus:border-purple-400 text-white placeholder-purple-300/50 focus:outline-none"
                rows={3}
              />

              <button
                onClick={handleSaveAndComplete}
                className="w-full mt-6 py-3 rounded-xl font-medium bg-purple-500 text-white hover:bg-purple-600"
              >
                Complete & Rest 🌙
              </button>
            </StepContainer>
          )}

          {/* Complete */}
          {currentStep === 'complete' && (
            <StepContainer key="complete" isMorning={isMorning}>
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.5 }}
                  className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
                    isMorning ? 'bg-amber-100' : 'bg-purple-500/20'
                  }`}
                >
                  <Check className={`w-10 h-10 ${isMorning ? 'text-amber-600' : 'text-purple-400'}`} />
                </motion.div>

                <h2 className={`text-2xl font-bold mb-3 ${isMorning ? 'text-amber-900' : 'text-white'}`}>
                  {isMorning ? 'You\'re aligned ✨' : 'Rest well 🌙'}
                </h2>
                
                <p className={`mb-8 ${isMorning ? 'text-amber-700/80' : 'text-purple-200/80'}`}>
                  {isMorning 
                    ? "Go create something beautiful today."
                    : "You've done enough. Tomorrow is a new opportunity."}
                </p>

                <button
                  onClick={onComplete}
                  className={`px-8 py-3 rounded-xl font-medium ${
                    isMorning 
                      ? 'bg-amber-500 text-white hover:bg-amber-600' 
                      : 'bg-purple-500 text-white hover:bg-purple-600'
                  }`}
                >
                  Done
                </button>
              </div>
            </StepContainer>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function StepContainer({ 
  children, 
  isMorning 
}: { 
  children: React.ReactNode; 
  isMorning: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

export default DailyAlignment;
